import { useMutation } from '@tanstack/react-query';

import {
  createTreatmentPeriodListPdfPreviewTicket,
  postTreatmentPeriodCsv,
  postTreatmentPeriodLabelPdf,
  type BinaryDownload,
} from '@/services/main/consent/treatmentPeriod';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';
import type { ConsentTreatmentPeriodPrintPayload } from '@/types/consentTreatmentPeriod.types';

/** Open the given blob in a new browser tab (PDF inline view). */
function openBlobInNewTab(result: BinaryDownload): void {
  const url = URL.createObjectURL(result.blob);
  const opened = window.open(url, '_blank');
  if (!opened) {
    // Popup blocked — fall back to download.
    triggerDownload(result);
  }
  // Revoke after a delay so the opened tab has time to load the content.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function openUrlInNewTab(url: string): void {
  const opened = window.open(url, '_blank');
  if (opened) return;

  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Trigger a file download (for CSV or PDF when popups are blocked). */
function triggerDownload(result: BinaryDownload): void {
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const useConsentTreatmentPeriodPrint = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const onError = (err: unknown) => {
    toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
  };

  const listPdf = useMutation({
    mutationFn: (payload: ConsentTreatmentPeriodPrintPayload) =>
      createTreatmentPeriodListPdfPreviewTicket(tenant ?? '', clinicNumber ?? '', serviceType, payload),
    onSuccess: openUrlInNewTab,
    onError,
  });

  const labelPdf = useMutation({
    mutationFn: (payload: ConsentTreatmentPeriodPrintPayload) =>
      postTreatmentPeriodLabelPdf(tenant ?? '', clinicNumber ?? '', serviceType, payload),
    onSuccess: openBlobInNewTab,
    onError,
  });

  const csv = useMutation({
    mutationFn: (payload: ConsentTreatmentPeriodPrintPayload) =>
      postTreatmentPeriodCsv(tenant ?? '', clinicNumber ?? '', serviceType, payload),
    onSuccess: triggerDownload,
    onError,
  });

  return { listPdf, labelPdf, csv };
};

export default useConsentTreatmentPeriodPrint;
